// 2) Third-party library imports
import { fetchAuthSession } from '@aws-amplify/auth';
import { get, map, filter as lFilter, isUndefined, isEmpty } from 'lodash-es';
import { all, call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';

// 3a) Local imports � Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from '../../config/config';
import { DETAILS_TAB, PAYMENTS_PAGE } from '../../config/tableAndPageConstants';
import { FamilyNameAttribute, GivenNameAttribute } from '../../constants/authUserAttributes';

// 3c) Local imports � Project-specific modules / components / types
import { _queries as queries } from '../../graphql/queries.graphql';
import { appliedFilterIndicator } from '../../constants/filterbar';
import { PageData } from '../common/types';
import { Invoice } from '../invoices/types';
import { SalesOrder } from '../salesOrders/types';
import { Payment, PaymentsActionTypes } from './types';
import type { ApplicationState } from '../storeTypes';

// 3d) Local imports � Utility functions / interfaces
import dayjs from 'src/config/dayjs';
import {
    checkIfEmailIsValid,
    checkShouldRequestRefetch,
    customPostData,
    formatDateToDateObjectUTC,
    getRegionallyApiResponse,
    getRegionallyGraphqlResponse,
    getSortFieldsWithCustomFields,
    populateSortFieldsParamSortParams,
    removeAppliedFiltersForApiRequest,
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import client, { post } from 'src/api';

// 3e) Local imports � Redux actions & selectors
import { getCurrentUser } from '../auth/authSlice';
import { getCloudImportType } from '../companies/companySlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import {
    addPaymentForADVAMRequestAction,
    addPaymentForEziDebitRequestAction,
    addPaymentForIntegraPayRequestAction,
    addPaymentForUrlRequestAction,
    addPaymentForWesternUnionRequestAction,
    getPaymentChangesErrorAction,
    getPaymentChangesSuccessAction,
    getPaymentConversationErrorAction,
    getPaymentConversationSuccessAction,
    getPaymentDataErrorAction,
    getPaymentDataSuccessAction,
    getPaymentInvoicesSuccessAction,
    getPaymentSalesOrdersErrorAction,
    getPaymentSalesOrdersSuccessAction,
    getPaymentsErrorAction,
    getPaymentsRequestAction,
    getPaymentsSuccessAction,
    setPaymentSelectedIdSuccessAction,
    deallocatePaymentRequestAction,
    getBankFileFormatsSuccessAction,
    getBankFileFormatsErrorAction,
    organisationPaymentDisregardTasksRequest,
    paymentDisregardTasksRequest,
    createOrganisationCreditAsOverpaymentTasksRequest,
    createCreditAsOverpaymentTasksRequest,
    getPaymentsCustomFieldsForCompanyRequestAction,
    getPaymentsCustomFieldsForOrganisationRequestAction,
    checkLastPaymentChangeLineRequestAction,
    sendInstructionPaymentForCorpayRequestAction,
    bookDealRequestAction,
    getPaymentSpotRateAction,
    addPaymentForCorpayRequestAction,
    paymentAddCommentRequestAction,
    getOrganisationPaymentChangesRequestAction,
    getPaymentChangesRequestAction,
    getPaymentConversationRequestAction,
    getOrganisationPaymentConversationRequestAction,
    getPaymentInvoicesRequestAction,
    setPaymentSelectedIdRequestAction,
    getBankFileFormatsRequestAction,
    getPaymentDataRequestAction,
    getPaymentsPageData,
    getPaymentSalesOrdersRequestAction,
    addPaymentForXeroRequestAction,
    addPaymentForConveraRequestAction
} from './paymentSlice';

let refetchCount = 0;

const preparePayloadForTaskFilter = ({ payload, canExcludeTasks, parseCustomFields }: { 
    payload: any, 
    canExcludeTasks: boolean,
    parseCustomFields: boolean
 }) => {
    const {
        filter,
        filters,
        sortBy,
        sortAscending,
        usingMultipleWorkflow,
        isPaymentPlanEnabled,
        taskIds,
        excludeTasks,
        recordLimit
    } = payload;

    const finalPayload: any = {
        Ascending: sortAscending,
        ...(
            canExcludeTasks ? {
                TaskIds: taskIds,
                ExcludeTasks: excludeTasks,
                RecordLimit: recordLimit
            } : {}
        )
    };
    const finalFilter = filter || filters; // Handle different filter property names in models

    if (finalFilter) {
        const typeValue = isPaymentPlanEnabled
            ? finalFilter.Type
            : lFilter(finalFilter.Type, (ft: any) => ft !== 8);
        const usedFilters = {
            ...finalFilter,
            Type: typeValue,
            [`Type${appliedFilterIndicator}`]: typeValue,
        };
        const cleanFilters = removeAppliedFiltersForApiRequest(
            usedFilters,
            true,
            'task'
        );
        const assignedUserValue = cleanFilters.AssignedUserId;

        if (assignedUserValue && checkIfEmailIsValid(assignedUserValue)) {
            delete cleanFilters.AssignedUserId;
            cleanFilters.AssignedEmailAddress = assignedUserValue;
        }

        if (parseCustomFields && cleanFilters.CustomFieldFilters) {
            cleanFilters.CustomFieldFilters = JSON.parse(
                cleanFilters.CustomFieldFilters
            );
        }

        if (!usingMultipleWorkflow) {
            delete cleanFilters.WorkflowIds;
        }

        Object.assign(finalPayload, cleanFilters);
    }

    if (sortBy) {
        const sortFields = populateSortFieldsParamSortParams(sortBy);

        if (parseCustomFields && sortFields.CustomFieldSort) {
            sortFields.CustomFieldSort = JSON.parse(
                sortFields.CustomFieldSort
            );
        }

        Object.assign(finalPayload, sortFields);
    }

    return finalPayload;
}

/**
 * Function that calls the API for fetching the payment list.
 * @param param0
 */
function* handleGetPaymentsRequest({ payload }: any) {
    const errorMessage = "Error fetching payments list. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            ActiveState,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true, 'payments');
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENTS_FOR_COMPANY, 
                variables: {
                    ...cleanFilters,
                    // SortField: sortBy,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * PAYMENTS_PAGE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    ActiveState
                }
            }
        );

        const { Payments } = get(res.data, 'GetPaymentsForCompany');
        const responsePayload = {
            data: Payments,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(Payments.length < pageSize) &&
                    !(pageSize < PAYMENTS_PAGE.pageSize),
            },
        };

        refetchCount = 0;

        yield put(getPaymentsSuccessAction(responsePayload));
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getPaymentsRequestAction(payload));
        } else {
            yield put(getPaymentsErrorAction([errorMessage]));
        }
    }
}

/**
 * Function calling the API for fetching the payment data based on the given id.
 * @param param0
 */
function* handleGetPaymentDataRequest({
    payload: { 
        paymentId, 
        isUsingCloudImportType, 
        companyId 
    },
}: any) {
    const errorMessage = "Error fetching payment details. Please try again later.";

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_PAYMENT_DETAILS_FOR_COMPANY, 
            {
                PaymentId: paymentId,
                CompanyId: companyId,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            }
        );

        const Payment = get(res.data, 'GetPaymentDetailsForCompany');

        if (Payment) {
            const responsePayload = {
                record: Payment,
            };

            yield put(getPaymentDataSuccessAction(responsePayload));
        } else {
            yield put(getPaymentDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getBankFileFormatsErrorAction([errorMessage]));
    }
}

/**
 * Function calling the API for fetching the company bank file format based on the company id.
 * @param param0
 */
function* handleGetBankFileFormatsRequest() {
    const errorMessage = "Error fetching company bank file format. Please try again later.";

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_BANK_FILE_FORMATS_FOR_COMPANY
            }
        );

        const bankFileFormats = get(res.data, 'GetBankFileFormatsForCompany');

        if (bankFileFormats) {
            const responsePayload = {
                bankFileFormats: bankFileFormats
            };

            yield put(getBankFileFormatsSuccessAction(responsePayload));
        } else {
            yield put(getBankFileFormatsErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getBankFileFormatsErrorAction([errorMessage]));
    }
}

/**
 * Function that sets the selected paymentId id for reference.
 * @param param0
 */
function* handleSetPaymentSelectedIdRequest({ payload }: any) {
    const { paymentId, callback } = payload;

    yield put(setPaymentSelectedIdSuccessAction(paymentId));

    if (callback) {
        callback();
    }
}

/**
 * Function that gets the invoices list for a certain payment.
 * @param param0
 */
function* handleGetPaymentInvoicesRequest({ payload }: any) {
    const errorMessage = "Error fetching payment's invoice list. Please try again later.";

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_ALLOCATED_INVOICES_FOR_PAYMENT,
            {
                ...cleanFilters,
                InvoiceState: invoiceState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
            }
        );

        const { PaymentInvoices } = get(res.data, 'GetAllocatedInvoicesForPayment');
        let invoices: Invoice[] = [];

        if (PaymentInvoices) {
            invoices = PaymentInvoices.map((inv: any) => inv.Invoice);
        }

        const responsePayload = {
            data: invoices,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(invoices.length < pageSize),
            },
        };

        yield put(getPaymentInvoicesSuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentConversationErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the salesOrders list for a certain payment.
 * @param param0
 */
function* handleGetPaymentSalesOrdersRequest({ payload }: any) {
    const errorMessage = "Error fetching payment's sales orders list. Please try again later.";

    try {
        const {
            filters,
            salesOrderState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_ALLOCATED_INVOICES_FOR_PAYMENT,
                variables: {
                    ...cleanFilters,
                    InvoiceState: salesOrderState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.SALES_ORDER_LIST_COMPLETE.pageSize,
                }
            }
        );

        const { PaymentInvoices } = get(res.data, 'GetAllocatedInvoicesForPayment');

        if (PaymentInvoices) {
            const SalesOrders: SalesOrder[] = PaymentInvoices.map((inv: any) => inv.Invoice);
            const responsePayload = {
                data: SalesOrders,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(SalesOrders.length < pageSize),
                },
            };

            yield put(getPaymentSalesOrdersSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentSalesOrdersErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given payment.
 * @param param0
 */
function* handleGetOrganisationPaymentConversationRequest({ payload }: any) {
    const errorMessage = "Error fetching organisation payment's conversation list. Please try again later.";

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            undefined,
            true
        );

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CONVERSATION_LINES_FOR_ORGANISATION, 
            {
                ...cleanFilters,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForOrganisation'
        );
        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getPaymentConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given payment.
 * @param param0
 */
function* handleGetPaymentConversationRequest({ payload }: any) {
    const errorMessage = "Error fetching payment's conversation list. Please try again later.";

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            undefined,
            true
        );

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CONVERSATION_LINES_FOR_COMPANY, 
            {
                ...cleanFilters,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForCompany'
        );

        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getPaymentConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentChangesErrorAction([errorMessage]));
    }
}

function* handleOrganisationPaymentChangesRequest({ payload }: any) {
    const errorMessage = "Error fetching payment changes list. Please try again later.";

    try {
        const { filters, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CHANGE_LINES_FOR_ORGANISATION,
            {
                ...cleanFilters,
                PageSize: pageSize,
                Skip:
                    currentPage * DETAILS_TAB.PAYMENT_CHANGES_TIMELINE.pageSize,
            }
        );

        const { ChangeLines } = get(res.data, 'GetChangeLinesForOrganisation');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.PAYMENT_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getPaymentChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentChangesErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the paymentChanges list of a given payment.
 * @param param0
 */
function* handleGetPaymentChangesRequest({ payload }: any) {
    const errorMessage = "Error fetching payment changes list. Please try again later.";

    try {
        const { filters, pageSize, currentPage } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_CHANGE_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    PageSize: pageSize,
                    Skip:
                        currentPage * DETAILS_TAB.PAYMENT_CHANGES_TIMELINE.pageSize,
                }
            }
        );
        const { ChangeLines } = get(res.data, 'GetChangeLinesForCompany');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.PAYMENT_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getPaymentChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getPaymentChangesErrorAction([errorMessage]));
    }
}

/**
 * Function for adding comment to the payment's conversation list.
 * @param param0
 */
function* handlePaymentAddCommentRequest({ payload: sagaPayload }: any) {
    const { filter, paymentIds, excludePayments, comment, callback } = sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(filter, true);

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        PaymentManagementFilter: {
            ...cleanFilters,
            PaymentIds: paymentIds,
            ExcludePayments: excludePayments,
        },
        Comment: comment,
    };

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/conversation/save/paymentcomment',
            'POST',
            payload
        );
        
        if (callback) {
            let RefetchList = true;

            if (paymentIds.length === 1 && excludePayments === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const paymentsUpdated: DynamicObject[] = map(
                    paymentIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getPaymentsPageData);

                yield put(
                    getPaymentsSuccessAction({
                        data: paymentsUpdated as Payment[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called when connecting add payment for payment url to the API.
 * @param param0
 */
function* handleAddPaymentForUrlRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/fromurl',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(addPaymentForUrlRequestAction(payload, callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function called when connecting add payment for western union to the API.
 * @param param0
 */
function* handleAddPaymentForWesternUnionRequest({
    payload: sagaPayload,
}: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/westernunion',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(
                addPaymentForWesternUnionRequestAction(payload, callback)
            );
        } else if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting add payment for EziDebit to the API.
 * @param param0
 */
function* handleAddPaymentForEziDebitRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/ezidebit',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(addPaymentForEziDebitRequestAction(payload, callback));
        } else if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting add payment for IntegraPay to the API.
 * @param param0
 */
function* handleAddPaymentForIntegraPayRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/integrapay',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(addPaymentForIntegraPayRequestAction(payload, callback));
        } else if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting add payment for ADVAM to the API.
 * @param param0
 */
function* handleAddPaymentForADVAMRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/advam',
            options: {
                body: payload,
            }
        })
        
        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };
            
            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(addPaymentForADVAMRequestAction(payload, callback));
        } else if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting add payment for western union to the API.
 * @param param0
 */
function* handleAddPaymentForCorpayRequest({
    payload: sagaPayload,
}: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/corpay/instructdeal',
            options: {
                body: payload
            }
        });

        //refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        // if (
        //     refetchCount <= maxAPIRefetchCount &&
        //     checkShouldRequestRefetch(err)
        // ) {
        //     refetchCount++;
        //     yield delay(refetchAPIDelay);
        //     yield put(
        //         addPaymentForWesternUnionRequestAction(payload, callback)
        //     );
        // } else {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.data) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
        // }
    }
}

/**
 * Function called when connecting add payment for Xero to the API.
 * @param param0
 */
function* handleAddPaymentForXeroRequest({
    payload: sagaPayload,
}: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/xero',
            options: {
                body: payload
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
        // }
    }
}

/**
 * Function called when getting the spot rate for corpay from the API .
 * @param param0
 */
function* handleGetCorpayPaymentSpotRate({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/corpay/spotrate',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        // callback(err)
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        // if (
        //     refetchCount <= maxAPIRefetchCount &&
        //     checkShouldRequestRefetch(err)
        // ) {
        //     refetchCount++;
        //     yield delay(refetchAPIDelay);
        //     yield put(addPaymentForADVAMRequestAction(payload, callback));
        // } else {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting book deal for Corpay to the API.
 * @param param0
 */
function* handleCorpayBookDealRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/corpay/bookdeal',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        // callback(err)
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting book deal for Corpay to the API.
 * @param param0
 */
 function* handleSendPaymentInstructionForCorpayRequest({ payload: sagaPayload }: any) {
     const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/sendreceipt',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        // callback(err)
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when connecting deallocate payment to the API.
 * @param param0
 */
function* handleDeallocatePaymentRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    const { PaymentId, CompanyId } = payload;
    let variables: any = { PaymentId };

    if (!isUndefined(CompanyId) && !isEmpty(CompanyId)) {
        variables.CompanyId = CompanyId
    }

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/change/payment/reverse',
            'POST',
            variables
        );

        refetchCount = 0;

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(deallocatePaymentRequestAction(payload, callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

/**
 * Function for check last payment change line that can be deallocated
 * @param param0
 */
function* handleCheckLastPaymentChangeLineRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    const { filters } = payload;

    try {
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true, 'changelines');
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.CHECK_LAST_PAYMENT_CHANGELINES,
            {
                ...cleanFilters,
            }
        );

        const { IsLastPaymentChangeline } = get(res.data, 'CheckLastPaymentChangeLines');
        const response = {
            IsSuccess: true,
            IsLastPaymentChangeline
        };

        callback(response);
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the payments custom fields for a company.
 * @param param0
 */
function* handleGetPaymentsCustomFieldsForCompanyRequest({ payload }: any) {
    const errorMessage = '';
    const { 
        CompanyId, 
        FieldName,
        callback
    } = payload;

    try {
        const errorMessage = "Error fetching custom field values. Please try again later.";

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENTS_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetPaymentsCustomFieldsForCompany');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) callback([]);

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the payments custom fields for a company.
 * @param param0
 */
function* handleGetPaymentsCustomFieldsForOrganisationRequest({ payload }: any) {
    const errorMessage = '';
    const { 
            CompanyIds, 
            FieldName,
            callback
        } = payload;
    try {
        const errorMessage =
        'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENTS_CUSTOM_FIELDS_FOR_ORGANISATION,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetPaymentsCustomFieldsForOrganisation');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when creating credit as overpayment
 * @param param0
 */
function* handleCreateCreditAsOverpaymentRequest({ payload: sagaPayload }: any) {
    const {
        CompanyId,
        OverpaymentCode,
        callback,
    } = sagaPayload;

    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId},
        OverpaymentCode
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/over-payment-credit',
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleCreateOrganisationCreditAsOverpaymentRequest({ payload: sagaPayload }: any) {
    const {
        CompanyId,
        callback,
    } = sagaPayload;

    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId },
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );
    
    try {
        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/over-payment-credit`,
                payload, { Authorization: accessToken }
            );
        } else {
            yield call(post, {
                apiName: API_NAME,
                path:'/task/action/organisation/over-payment-credit',
                options: {
                    body: payload,
                }
            });
        }

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when confirming a remittance advice disregard tasks.
 * @param param0
 */
function* handlePaymentDisregardTasksRequest({ payload: sagaPayload }: any) {
    const {
        CompanyId,
        callback,
    } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId },
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/disregard/payment',
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleOrganisationPaymentDisregardTasksRequest({ payload: sagaPayload }: any) {
    const {
        CompanyId,
        callback,
    } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter, CompanyId },
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/disregard/payment`,
                payload, { Authorization: accessToken }
            );
        } else {
            yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/disregard/payment',
                options: {
                    body: payload,
                }
            });
        }
        
        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when connecting add payment for Convera to the API.
 * @param param0
 */
function* handleAddPaymentForConveraRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/payment/convera',
            options: {
                body: payload,
            }
        });

        refetchCount = 0;
        const res: DynamicObject = yield body.json();

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };
            callback(response);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(addPaymentForConveraRequestAction(payload, callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.data) {
                    returnData = (err as any).response.data;
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetPaymentsRequest() {
    yield takeLatest(
        getPaymentsRequestAction.type,
        handleGetPaymentsRequest
    );
}

function* watchGetPaymentDataRequest() {
    yield takeLatest(
        getPaymentDataRequestAction.type,
        handleGetPaymentDataRequest
    );
}

function* watchGetBankFileFormatsRequest() {
    yield takeLatest(
        getBankFileFormatsRequestAction.type,
        handleGetBankFileFormatsRequest
    );
}

function* watchSetPaymentSelectedIdRequest() {
    yield takeLatest(
        setPaymentSelectedIdRequestAction.type,
        handleSetPaymentSelectedIdRequest
    );
}

function* watchGetPaymentInvoicesRequest() {
    yield takeLatest(
        getPaymentInvoicesRequestAction.type,
        handleGetPaymentInvoicesRequest
    );
}

function* watchGetPaymentSalesOrdersRequest() {
    yield takeLatest(
        getPaymentSalesOrdersRequestAction.type,
        handleGetPaymentSalesOrdersRequest
    );
}

function* watchGetOrganisationPaymentConversationRequest() {
    yield takeLatest(
        getOrganisationPaymentConversationRequestAction.type,
        handleGetOrganisationPaymentConversationRequest
    );
}

function* watchGetPaymentConversationRequest() {
    yield takeLatest(
        getPaymentConversationRequestAction.type,
        handleGetPaymentConversationRequest
    );
}

function* watchGetPaymentChangesRequest() {
    yield takeLatest(
        getPaymentChangesRequestAction.type,
        handleGetPaymentChangesRequest
    );
}

function* watchGetOrganisationPaymentChangesRequest() {
    yield takeLatest(
        getOrganisationPaymentChangesRequestAction.type,
        handleOrganisationPaymentChangesRequest
    );
}

function* watchPaymentAddCommentRequest() {
    yield takeLatest(
        paymentAddCommentRequestAction.type,
        handlePaymentAddCommentRequest
    );
}

function* watchAddPaymentForUrlRequestRequest() {
    yield takeLatest(
        addPaymentForUrlRequestAction.type,
        handleAddPaymentForUrlRequest
    );
}

function* watchAddPaymentForWesternUnionRequestRequest() {
    yield takeLatest(
        addPaymentForWesternUnionRequestAction.type,
        handleAddPaymentForWesternUnionRequest
    );
}

function* watchAddPaymentForEziDebitRequestRequest() {
    yield takeLatest(
        addPaymentForEziDebitRequestAction.type,
        handleAddPaymentForEziDebitRequest
    );
}

function* watchAddPaymentForIntegraPayRequestRequest() {
    yield takeLatest(
        addPaymentForIntegraPayRequestAction.type,
        handleAddPaymentForIntegraPayRequest
    );
}

function* watchAddPaymentForADVAMRequestRequest() {
    yield takeLatest(
        addPaymentForADVAMRequestAction.type,
        handleAddPaymentForADVAMRequest
    );
}

function* watchAddPaymentForCorpayRequestRequest() {
    yield takeLatest(
        addPaymentForCorpayRequestAction.type,
        handleAddPaymentForCorpayRequest
    );
}

function* watchAddPaymentForXeroRequestRequest() {
    yield takeLatest(
        addPaymentForXeroRequestAction.type,
        handleAddPaymentForXeroRequest
    );
}

function* watchGetCorpayPaymentSpotRate() {
    yield takeLatest(
        getPaymentSpotRateAction.type,
        handleGetCorpayPaymentSpotRate
    );
}

function* watchCorpayBookDealRequest() {
    yield takeLatest(
        bookDealRequestAction.type,
        handleCorpayBookDealRequest
    );
}

function* watchSendPaymentInstructionForCorpayRequestRequest() {
    yield takeLatest(
        sendInstructionPaymentForCorpayRequestAction.type,
        handleSendPaymentInstructionForCorpayRequest
    );
}

function* watchDeallocatePaymentRequest() {
    yield takeLatest(
        deallocatePaymentRequestAction.type,
        handleDeallocatePaymentRequest
    );
}


function* watchCheckLastPaymentChangeLineRequest() {
    yield takeLatest(
        checkLastPaymentChangeLineRequestAction.type,
        handleCheckLastPaymentChangeLineRequest
    );
}

function* watchGetPaymentsCustomFieldsForCompanyRequest() {
    yield takeLatest(
        getPaymentsCustomFieldsForCompanyRequestAction.type,
        handleGetPaymentsCustomFieldsForCompanyRequest
    );
}

function* watchGetPaymentsCustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getPaymentsCustomFieldsForOrganisationRequestAction.type,
        handleGetPaymentsCustomFieldsForOrganisationRequest
    );
}

function* watchCreateCreditAsOverpaymentRequest() {
    yield takeLatest(
        createCreditAsOverpaymentTasksRequest.type,
        handleCreateCreditAsOverpaymentRequest
    );
}

function* watchCreateOrganisationCreditAsOverpaymentRequest() {
    yield takeLatest(
        createOrganisationCreditAsOverpaymentTasksRequest.type,
        handleCreateOrganisationCreditAsOverpaymentRequest
    );
}

function* watchPaymentDisregardTasksRequest() {
    yield takeLatest(
        paymentDisregardTasksRequest.type, 
        handlePaymentDisregardTasksRequest
    );
}

function* watchOrganisationPaymentDisregardTasksRequest() {
    yield takeLatest(
        organisationPaymentDisregardTasksRequest.type, 
        handleOrganisationPaymentDisregardTasksRequest
    );
}

function* watchAddPaymentForConveraRequestRequest() {
    yield takeLatest(
        addPaymentForConveraRequestAction.type,
        handleAddPaymentForConveraRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* paymentsSaga() {
    yield all([
        fork(watchGetPaymentsRequest),
        fork(watchGetPaymentDataRequest),
        fork(watchGetBankFileFormatsRequest),
        fork(watchSetPaymentSelectedIdRequest),
        fork(watchGetPaymentInvoicesRequest),
        fork(watchGetPaymentSalesOrdersRequest),
        fork(watchGetPaymentConversationRequest),
        fork(watchGetPaymentChangesRequest),
        fork(watchPaymentAddCommentRequest),
        fork(watchAddPaymentForUrlRequestRequest),
        fork(watchAddPaymentForWesternUnionRequestRequest),
        fork(watchAddPaymentForEziDebitRequestRequest),
        fork(watchAddPaymentForIntegraPayRequestRequest),
        fork(watchAddPaymentForADVAMRequestRequest),
        fork(watchGetCorpayPaymentSpotRate),
        fork(watchCorpayBookDealRequest),
        fork(watchAddPaymentForCorpayRequestRequest),
        fork(watchAddPaymentForXeroRequestRequest),
        fork(watchSendPaymentInstructionForCorpayRequestRequest),
        fork(watchDeallocatePaymentRequest),
        fork(watchCheckLastPaymentChangeLineRequest),
        fork(watchGetPaymentsCustomFieldsForCompanyRequest),
        fork(watchGetPaymentsCustomFieldsForOrganisationRequest),
        fork(watchCreateCreditAsOverpaymentRequest),
        fork(watchCreateOrganisationCreditAsOverpaymentRequest),
        fork(watchPaymentDisregardTasksRequest),
        fork(watchOrganisationPaymentDisregardTasksRequest),
        fork(watchGetOrganisationPaymentConversationRequest),
        fork(watchGetOrganisationPaymentChangesRequest),
        fork(watchAddPaymentForConveraRequestRequest)
    ]);
}

export default paymentsSaga;